type NewRelicAttributeValue = string | number | null

export type NewRelicAttributes = Record<string, NewRelicAttributeValue>

export type NewRelicErrorContext =
  | 'ACCOUNT-SUSPENDED-PAGE'
  | 'APP'
  | 'AUTH-PAGES'
  | 'FIRST-PARTY-REVIEWS'
  | 'MARKET-INTELLIGENCE'
  | 'OMNI-LOCATION'
  | 'SOCIAL-MANAGEMENT'
  | 'WIDGET-PREVIEW'
  | 'WIDGETS'
  | 'BILLING-AND-PAYMENTS'
  | 'UPGRADE-PACKAGE'
  | 'REPORTS'
  | 'CONNECTIONS'
  | 'ONBOARDING'
  | 'MENU_CHOICES'
  | 'MENUS'
  | 'MENU_SECTIONS'
  | 'MENU_ITEMS'
  | 'MENU_MODIFIERS'
  | 'REVIEWS'

const TRACKED_CUSTOM_ATTRIBUTES: Set<string> = new Set()

interface IgnoreRule {
  statusCode?: number
  message?: string | RegExp
  route?: string | RegExp
}

interface MonitorableError {
  message?: string
  statusCode?: number
  status?: number
  url?: string
  response?: {
    status?: number
    data?: unknown
  }
  config?: {
    url?: string
  }
}

const IGNORED_KNOWN_ERRORS: IgnoreRule[] = [
  { statusCode: 422 },
  { statusCode: 404, route: /\/api\/v1\/managed-reviews\/prompts\/review\/.*/ },
  { message: 'Network Error' },
]

const safeStringify = (data: unknown) => {
  try {
    return JSON.stringify(data)
  } catch {
    return 'Could not stringify body'
  }
}

const isMonitorableError = (err: unknown): err is MonitorableError => {
  return typeof err === 'object' && err !== null
}

const extractErrorData = (err: unknown) => {
  if (isMonitorableError(err)) {
    return {
      message: err.message || '',
      statusCode: err.response?.status || err.statusCode || err.status,
      url: err.config?.url || err.url || window?.location?.pathname,
      body: err.response?.data,
    }
  }

  return {
    message: String(err ?? ''),
    statusCode: undefined,
    url: window?.location?.pathname,
    body: undefined,
  }
}

const errorHandler = (err: unknown): boolean => {
  try {
    const { message, statusCode, url } = extractErrorData(err)

    return IGNORED_KNOWN_ERRORS.some((rule) => {
      if (rule.statusCode === undefined && rule.message === undefined && rule.route === undefined) {
        return false
      }

      if (rule.statusCode !== undefined && rule.statusCode !== statusCode) {
        return false
      }

      if (rule.message !== undefined) {
        if (rule.message instanceof RegExp) {
          if (!rule.message.test(message)) return false
        } else if (!message.includes(rule.message)) {
          return false
        }
      }

      if (rule.route !== undefined) {
        const safeUrl = String(url || '')
        if (rule.route instanceof RegExp) {
          if (!rule.route.test(safeUrl)) return false
        } else if (!safeUrl.includes(rule.route)) {
          return false
        }
      }

      if (import.meta.env.DEV) {
        console.warn('NewRelic: Ignored error due to rule:', rule, { message, statusCode, url })
      }

      // Log the ignored error as a PageAction for observability without triggering alerts
      window.newrelic?.addPageAction?.('IgnoredError', {
        rule: JSON.stringify(rule),
        message: message || '',
        statusCode: statusCode ? String(statusCode) : '',
        url: url || '',
      })

      return true
    })
  } catch (e) {
    console.warn('NewRelic errorHandler failed:', e)
    return false
  }
}

export const NewRelic = {
  identify: ({
    userId,
    currentAccountId,
    originalAccountId,
    isMarqiiUser,
    isImpersonating,
  }: {
    userId: string
    currentAccountId: string
    originalAccountId: string | null
    isMarqiiUser: boolean
    isImpersonating: boolean
  }) => {
    window?.newrelic?.setUserId?.(userId)
    NewRelic.setCustomAttribute('currentAccountId', currentAccountId)
    NewRelic.setCustomAttribute('originalAccountId', originalAccountId)
    NewRelic.setCustomAttribute('isMarqiiUser', String(isMarqiiUser))
    NewRelic.setCustomAttribute('isImpersonating', String(isImpersonating))
  },

  init: () => {
    window?.newrelic?.setErrorHandler?.(errorHandler)
  },

  setCustomAttribute: (name: string, value: NewRelicAttributeValue) => {
    if (value !== null) {
      TRACKED_CUSTOM_ATTRIBUTES.add(name)
    } else {
      TRACKED_CUSTOM_ATTRIBUTES.delete(name)
    }
    window?.newrelic?.setCustomAttribute?.(name, value)
  },

  clear: () => {
    window?.newrelic?.setUserId?.(null)
    TRACKED_CUSTOM_ATTRIBUTES.forEach((name) => {
      window?.newrelic?.setCustomAttribute?.(name, null)
    })
    TRACKED_CUSTOM_ATTRIBUTES.clear()
  },

  addPageAction: (name: string, attributes?: NewRelicAttributes) => {
    window?.newrelic?.addPageAction?.(name, attributes)

    if (import.meta.env.DEV) {
      console.warn('[NewRelic] addPageAction:', name, attributes)
    }
  },

  noticeError: (
    errorContext: NewRelicErrorContext,
    errorOrMessage: Error | string,
    attributes?: NewRelicAttributes,
  ) => {
    const error = typeof errorOrMessage === 'string' ? new Error(errorOrMessage) : errorOrMessage
    const { body } = extractErrorData(errorOrMessage)
    const errorMessage = body ? safeStringify(body) : undefined

    window?.newrelic?.noticeError?.(error, {
      errorContext,
      ...attributes,
      ...(errorMessage ? { errorMessage } : {}),
    })

    if (import.meta.env.DEV) {
      console.warn('[NewRelic] noticeError:', error, { errorContext, attributes, errorMessage })
    }
  },
}
